import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./style.scss";

const SearchDropdown = ({
  // Data props
  data = [],
  value = "",
  searchTerm = "",

  // Configuration props
  searchFields = ["name"], // Fields to search in
  displayField = "name", // Primary field to display
  secondaryField = null, // Secondary field to display (optional)
  valueField = null, // Field to use as value (if null, uses entire object)

  // UI props
  placeholder = "Search...",
  name = "",
  label = "",
  required = false,
  disabled = false,
  showLabel = true,
  showIcon = true,
  className = "",

  // Event handlers
  onChange,
  onSelect,
  onFocus,
  onBlur,

  // Validation
  error = "",
  state = null, // null, true, false for validation states

  // Advanced options
  maxResults = 100,
  minSearchLength = 0,
  noResultsText = "No results found",
  loadingText = "Loading...",
  isLoading = false,
  allowClear = true,
  customRenderer = null, // Custom function to render dropdown items
}) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState(searchTerm);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const dropdownRef = useRef(null);

  // Initialize search term and selected item
  useEffect(() => {
    setInternalSearchTerm(searchTerm);

    // Find selected item based on value
    if (value && data.length > 0) {
      let found = null;
      if (valueField) {
        found = data.find(
          (item) =>
            item[valueField] === value ||
            (typeof value === "string" &&
              JSON.stringify(item[valueField]) === value)
        );
      } else {
        found = data.find(
          (item) => JSON.stringify(item) === value || item === value
        );
      }
      setSelectedItem(found);
      if (found) {
        setInternalSearchTerm(found[displayField] || "");
      }
    } else if (!value) {
      setSelectedItem(null);
      setInternalSearchTerm("");
    }
  }, [value, searchTerm, data, displayField, valueField]);

  // Filter data based on search term
  useEffect(() => {
    if (!data || data.length === 0) {
      setFilteredData([]);
      return;
    }

    if (!internalSearchTerm || internalSearchTerm.length < minSearchLength) {
      setFilteredData(data.slice(0, maxResults));
      return;
    }

    const filtered = data
      .filter((item) => {
        return searchFields.some((field) => {
          const fieldValue = getNestedValue(item, field);
          return (
            fieldValue &&
            fieldValue
              .toString()
              .toLowerCase()
              .includes(internalSearchTerm.toLowerCase())
          );
        });
      })
      .slice(0, maxResults);

    setFilteredData(filtered);
  }, [internalSearchTerm, data, searchFields, minSearchLength, maxResults]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        if (onBlur) onBlur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onBlur]);

  // Helper function to get nested object values
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setInternalSearchTerm(newSearchTerm);
    setShowDropdown(true);
    setSelectedItem(null);

    if (onChange) {
      onChange({
        target: {
          value: newSearchTerm,
          name: event.target.name,
        },
        searchTerm: newSearchTerm,
        selectedItem: null,
      });
    }
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setInternalSearchTerm(item[displayField] || "");
    setShowDropdown(false);

    const itemValue = valueField ? item[valueField] : item;
    const finalValue =
      typeof itemValue === "object" ? JSON.stringify(itemValue) : itemValue;

    if (onSelect) {
      onSelect(item, finalValue);
    }

    if (onChange) {
      onChange({
        target: {
          value: finalValue,
          name: name,
        },
        searchTerm: item[displayField] || "",
        selectedItem: item,
      });
    }
  };

  // Handle focus
  const handleFocus = (event) => {
    setShowDropdown(true);
    if (onFocus) onFocus(event);
  };

  // Handle clear
  const handleClear = () => {
    setInternalSearchTerm("");
    setSelectedItem(null);
    setShowDropdown(false);

    if (onChange) {
      onChange({
        target: {
          value: "",
          name: name,
        },
        searchTerm: "",
        selectedItem: null,
      });
    }
  };

  // Get validation styles
  const getValidationClass = () => {
    if (error) return "error";
    if (state === true) return "success";
    if (state === false) return "error";
    return "";
  };

  const getIconStyle = () => {
    if (error || state === false) return "wrong";
    if (state === true) return "correct";
    return "null";
  };

  const getIcon = () => {
    if (error || state === false) return "times-circle";
    if (state === true) return "check-circle";
    return "check-circle";
  };

  // Default item renderer
  const defaultRenderer = (item, index) => (
    <div
      key={index}
      className="dropdown-item"
      onClick={() => handleItemSelect(item)}
    >
      <div className="primary-text">{item[displayField] || ""}</div>
      {secondaryField && (
        <div className="secondary-text">{item[secondaryField] || ""}</div>
      )}
    </div>
  );

  return (
    <div className={`search-dropdown-container ${className}`} ref={dropdownRef}>
      {showLabel && label && (
        <label htmlFor="search-input">
          {label} {required && <span className="required">*</span>}
        </label>
      )}

      {showIcon && (
        <FontAwesomeIcon
          className={`validation-icon ${getIconStyle()}`}
          icon={["fas", getIcon()]}
        />
      )}

      <div className="search-dropdown">
        <div className="input-container">
          <input
            name={name}
            type="text"
            className={`form-control ${getValidationClass()}`}
            id="search-input"
            placeholder={placeholder}
            value={internalSearchTerm}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            disabled={disabled || isLoading}
            autoComplete="off"
          />

          {allowClear && internalSearchTerm && !disabled && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              title="Clear"
            >
              <FontAwesomeIcon icon={["fas", "times"]} />
            </button>
          )}
        </div>

        {showDropdown && !disabled && (
          <div className="dropdown-list">
            {isLoading ? (
              <div className="dropdown-item loading">{loadingText}</div>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) =>
                customRenderer
                  ? customRenderer(item, index, handleItemSelect)
                  : defaultRenderer(item, index)
              )
            ) : (
              <div className="dropdown-item no-results">{noResultsText}</div>
            )}
          </div>
        )}
      </div>

      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default SearchDropdown;
